<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ContactFlowEnrollmentsAPI from 'dashboard/api/contactFlowEnrollments';

const props = defineProps({
  contactId: { type: Number, default: null },
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const search = ref('');
const enrolledFlowIds = ref([]);
const loadingFlowIds = ref(new Set());
const highlightedIndex = ref(0);
const pendingFlowId = ref(null);

const CATEGORY_DOT_COLOR = {
  management: 'bg-n-blue-9',
  follow_up: 'bg-n-iris-9',
  nurturing: 'bg-n-amber-9',
  reminders: 'bg-n-teal-9',
};

function flowDotColor(flow) {
  return CATEGORY_DOT_COLOR[flow?.category] ?? 'bg-n-slate-6';
}

const activeFlows = computed(() =>
  (store.getters['flows/getFlows'] ?? []).filter(f =>
    ['active', 'paused'].includes(f.status)
  )
);
const filteredFlows = computed(() => {
  const q = search.value.toLowerCase().trim();
  const flows = q
    ? activeFlows.value
        .filter(f => f.name.toLowerCase().includes(q))
        .sort(
          (a, b) =>
            a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
        )
    : activeFlows.value;
  const enrolled = flows.filter(f => enrolledFlowIds.value.includes(f.id));
  const rest = flows.filter(f => !enrolledFlowIds.value.includes(f.id));
  return [...enrolled, ...rest];
});

const hasActiveFlows = computed(() => enrolledFlowIds.value.length > 0);

async function fetchEnrollments() {
  if (!props.contactId) {
    enrolledFlowIds.value = [];
    return;
  }
  try {
    const res = await ContactFlowEnrollmentsAPI.index(props.contactId);
    enrolledFlowIds.value = (res.data.data ?? []).map(e => e.flow_id);
  } catch {
    enrolledFlowIds.value = [];
  }
}

watch(() => props.contactId, fetchEnrollments, { immediate: true });
watch(filteredFlows, () => {
  highlightedIndex.value = 0;
});

async function addFlow(flowId) {
  if (loadingFlowIds.value.has(flowId)) return;
  loadingFlowIds.value = new Set([...loadingFlowIds.value, flowId]);
  try {
    await ContactFlowEnrollmentsAPI.create(props.contactId, flowId);
    if (!enrolledFlowIds.value.includes(flowId)) {
      enrolledFlowIds.value = [...enrolledFlowIds.value, flowId];
    }
  } finally {
    loadingFlowIds.value = new Set(
      [...loadingFlowIds.value].filter(id => id !== flowId)
    );
  }
}

async function removeFlow(flowId) {
  if (loadingFlowIds.value.has(flowId)) return;
  loadingFlowIds.value = new Set([...loadingFlowIds.value, flowId]);
  try {
    await ContactFlowEnrollmentsAPI.destroy(props.contactId, flowId);
    enrolledFlowIds.value = enrolledFlowIds.value.filter(id => id !== flowId);
  } finally {
    loadingFlowIds.value = new Set(
      [...loadingFlowIds.value].filter(id => id !== flowId)
    );
  }
}

function requestToggle(flowId) {
  if (loadingFlowIds.value.has(flowId)) return;
  pendingFlowId.value = pendingFlowId.value === flowId ? null : flowId;
}

function confirmToggle() {
  if (!pendingFlowId.value) return;
  const flowId = pendingFlowId.value;
  pendingFlowId.value = null;
  if (enrolledFlowIds.value.includes(flowId)) {
    removeFlow(flowId);
  } else {
    addFlow(flowId);
  }
}

function cancelToggle() {
  pendingFlowId.value = null;
}

function onKeydown(e) {
  if (!props.open) return;
  if (e.key === 'Escape') {
    if (pendingFlowId.value) {
      pendingFlowId.value = null;
    } else {
      emit('close');
      search.value = '';
    }
  } else if (pendingFlowId.value) {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmToggle();
    }
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlightedIndex.value = Math.min(
      highlightedIndex.value + 1,
      filteredFlows.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const flow = filteredFlows.value[highlightedIndex.value];
    if (flow) requestToggle(flow.id);
  } else if (e.key === 'Backspace') {
    search.value = search.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    search.value += e.key;
  }
}

watch(
  () => props.open,
  val => {
    if (val) {
      window.addEventListener('keydown', onKeydown);
    } else {
      window.removeEventListener('keydown', onKeydown);
      search.value = '';
      pendingFlowId.value = null;
    }
  },
  { flush: 'sync' }
);

onMounted(async () => {
  store.dispatch('flows/fetch');
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
});

defineExpose({ hasActiveFlows });
</script>

<template>
  <div v-if="open" class="relative" @click.stop>
    <div
      class="absolute right-0 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-full min-w-[200px] bottom-full mb-10"
      @click.stop
    >
      <div class="max-h-56 overflow-y-auto">
        <template v-for="(flow, idx) in filteredFlows" :key="flow.id">
          <!-- Item do flow -->
          <button
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none text-left"
            :class="
              pendingFlowId === flow.id
                ? 'bg-n-alpha-2'
                : idx === highlightedIndex
                  ? 'bg-n-alpha-1'
                  : 'hover:bg-n-alpha-1'
            "
            @click="requestToggle(flow.id)"
          >
            <!-- Checkbox estilo tarefas -->
            <span
              class="size-4 min-w-4 rounded flex items-center justify-center transition-all flex-shrink-0"
              :class="
                loadingFlowIds.has(flow.id)
                  ? 'border border-n-slate-6'
                  : pendingFlowId === flow.id
                    ? enrolledFlowIds.includes(flow.id)
                      ? 'bg-n-teal-9 border border-n-teal-9 opacity-60'
                      : 'border border-n-teal-6 bg-n-teal-2'
                    : enrolledFlowIds.includes(flow.id)
                      ? 'bg-n-teal-9 border border-n-teal-9'
                      : 'border border-n-slate-6 hover:border-n-teal-6 hover:bg-n-teal-2'
              "
            >
              <span
                v-if="loadingFlowIds.has(flow.id)"
                class="i-lucide-loader-circle size-2.5 text-n-slate-10 animate-spin"
              />
              <span
                v-else-if="enrolledFlowIds.includes(flow.id)"
                class="i-lucide-check size-2.5 text-white"
              />
            </span>

            <!-- Nome do flow -->
            <span
              class="flex-1 truncate"
              :class="
                enrolledFlowIds.includes(flow.id)
                  ? 'text-n-teal-11 font-medium'
                  : 'text-n-slate-12'
              "
              >{{ flow.name }}</span
            >

            <!-- Dot de categoria -->
            <span
              class="size-2 rounded-full flex-shrink-0"
              :class="flowDotColor(flow)"
            />
          </button>

          <!-- Barra de confirmação inline -->
          <div
            v-if="pendingFlowId === flow.id"
            class="flex items-center gap-2 px-3 py-2 border-t border-n-weak bg-n-surface-2"
          >
            <span class="flex-1 text-xs text-n-slate-11 truncate">
              {{
                enrolledFlowIds.includes(flow.id)
                  ? t('KANBAN.FLOWS_FIELD.CONFIRM_REMOVE')
                  : t('KANBAN.FLOWS_FIELD.CONFIRM_ADD')
              }}
            </span>
            <button
              type="button"
              class="text-xs font-medium px-2 py-0.5 rounded text-white hover:brightness-110 transition-all flex-shrink-0"
              :class="
                enrolledFlowIds.includes(flow.id)
                  ? 'bg-n-ruby-9'
                  : 'bg-n-teal-9'
              "
              @click.stop="confirmToggle"
            >
              {{ t('KANBAN.FLOWS_FIELD.CONFIRM_BTN') }}
            </button>
            <button
              type="button"
              class="text-xs font-medium px-2 py-0.5 rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-all flex-shrink-0"
              @click.stop="cancelToggle"
            >
              {{ t('KANBAN.FLOWS_FIELD.CANCEL_BTN') }}
            </button>
          </div>
        </template>

        <p
          v-if="!filteredFlows.length"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('KANBAN.FLOWS_FIELD.NO_FLOWS') }}
        </p>
      </div>
    </div>
  </div>
</template>
